import React from 'react';
import { Link } from 'react-router-dom';

// Trophées palette (mêmes valeurs que HomePage / FaqPage / MentionsLegalesPage)
const NAVY = '#1B1E38';
const CYAN = '#39A8E5';
const BLUE = '#4A71E5';

// Static content extracted from "Politique Confidentialite V2 IP.docx"
// (version en vigueur au 20 mai 2026). Edits should be made here directly.

// §4 retention durations table
const RETENTION_ROWS = [
  ['Dossiers de candidature (candidats non retenus et lauréats)', '5 ans à compter de la clôture du concours', 'Prescription civile — art. 2224 Code civil'],
  ['Documents comptables liés aux dotations', '10 ans', 'Art. L.123-22 Code de commerce'],
  ['Données de compte utilisateur', 'Durée du concours + 3 ans après inactivité', 'Durée raisonnable de conservation'],
  ['Données de connexion / logs', '12 mois', 'Recommandation CNIL'],
  ['Données de communication (emails, notifications)', '3 ans après le dernier contact (candidats non retenus) — 5 ans (lauréats)', 'Durée raisonnable / prescription civile'],
];

// §6 rights table
const RIGHTS_ROWS = [
  ["Droit d'accès (art. 15)", 'Obtenir confirmation que vos données sont traitées et en recevoir une copie', 'rgpd@investance-partners.com'],
  ['Droit de rectification (art. 16)', 'Corriger des données inexactes ou incomplètes', 'rgpd@investance-partners.com'],
  ["Droit à l'effacement (art. 17)", 'Demander la suppression de vos données dans les cas prévus par le RGPD', 'rgpd@investance-partners.com'],
  ['Droit à la limitation (art. 18)', 'Suspendre le traitement de vos données dans certains cas', 'rgpd@investance-partners.com'],
  ["Droit d'opposition (art. 21)", "Vous opposer à un traitement fondé sur l'intérêt légitime", 'rgpd@investance-partners.com'],
  ['Droit à la portabilité (art. 20)', 'Recevoir vos données dans un format structuré et lisible', 'rgpd@investance-partners.com'],
  ['Droit de réclamation', "Saisir la CNIL si vous estimez que vos droits ne sont pas respectés", 'www.cnil.fr'],
];

const EFFECTIVE_DATE = '20 mai 2026';
const LAST_UPDATE = '11 mai 2026';

export default function PolitiqueConfidentialitePage() {
  return (
    <div style={{ minHeight: '100vh', background: 'white' }}>
      {/* ===== Top bar ===== */}
      <header style={{
        background: NAVY, color: 'white', padding: '14px 32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'baseline', gap: 8, textDecoration: 'none', color: 'white' }}>
          <span style={{ fontFamily: 'Lato, sans-serif', fontWeight: 800, fontSize: 18, letterSpacing: '-0.3px' }}>Plateforme ISI</span>
          <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 13, color: CYAN }}>by Paradoxe.ai for LBP AM</span>
        </Link>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Link to="/faq" style={navLinkStyle}>FAQ</Link>
          <a href="mailto:contact.tropheeslbpam@investance-partners.com" style={navLinkStyle}>Nous contacter</a>
          <Link to="/login" style={navLinkStyle}>Se connecter</Link>
          <Link to="/register" style={{ ...navLinkStyle, color: 'white', background: BLUE, border: `1px solid ${BLUE}` }}>Créer un compte</Link>
        </nav>
      </header>

      {/* ===== Hero ===== */}
      <section style={{
        background: `linear-gradient(180deg, ${NAVY} 0%, #2A2E4E 100%)`,
        color: 'white', padding: '56px 32px 40px',
      }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <p style={{ fontSize: 13, color: CYAN, fontWeight: 600, letterSpacing: 1.2, marginBottom: 12, textTransform: 'uppercase' }}>
            Données personnelles &amp; RGPD
          </p>
          <h1 style={{ fontFamily: 'Lato, sans-serif', fontSize: 38, fontWeight: 800, lineHeight: 1.15, marginBottom: 12, marginTop: 0 }}>
            Politique de confidentialité
          </h1>
          <p style={{ fontSize: 16, lineHeight: 1.55, color: 'rgba(255,255,255,0.85)', maxWidth: 720, marginBottom: 0 }}>
            Plateforme Trophées de l'innovation 2026
            <br />
            <span style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.65)' }}>
              Investance Partners — Version en vigueur au {EFFECTIVE_DATE}.
            </span>
          </p>
        </div>
      </section>

      {/* ===== Body ===== */}
      <section style={{ padding: '48px 32px 64px', background: '#FAFBFD' }}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>

          {/* Intro callout */}
          <Callout>
            La protection de vos données personnelles est une priorité pour Investance Partners.
            La présente politique vous informe, de manière transparente et complète, de la façon
            dont nous collectons, utilisons, conservons et protégeons vos données dans le cadre
            des Trophées de l'innovation 2026, conformément au Règlement (UE) 2016/679 (RGPD) et
            à la loi Informatique et Libertés du 6 janvier 1978 modifiée.
          </Callout>

          {/* §1 */}
          <Section id={1} title="Identité du responsable de traitement">
            <P>Le responsable de traitement est <strong>Investance Partners</strong>.</P>
            <Def label="Adresse">Investance Partners, 31 rue Saint-Augustin, 75002 Paris — RCS Paris 435 257 175</Def>
            <Def label="E-mail" href="mailto:contact.lbpam@investance-partners.com">contact.lbpam@investance-partners.com</Def>
            <Def label="Contact RGPD" href="mailto:rgpd@investance-partners.com">rgpd@investance-partners.com</Def>
            <P>Pour toute question relative au traitement de vos données ou pour exercer vos droits, vous pouvez contacter directement Investance Partners aux adresses ci-dessus.</P>
          </Section>

          {/* §2 */}
          <Section id={2} title="Données collectées et finalités">
            <P>Dans le cadre des Trophées de l'innovation 2026, Investance Partners collecte et traite les données à caractère personnel suivantes :</P>

            <SubTitle>2.1 Données relatives à la création de compte</SubTitle>
            <Def label="Finalité">création et gestion de votre espace candidat sur la plateforme.</Def>
            <Def label="Base légale">exécution d'un contrat et licéité du traitement (article 6.1.b RGPD).</Def>
            <P style={{ marginBottom: 4 }}>Données collectées :</P>
            <Bullets items={[
              "Nom et prénom du représentant légal de l'entreprise ou du fondateur inscrit",
              'Adresse e-mail professionnelle',
              'Mot de passe (stocké sous forme hachée via l\'algorithme bcrypt avec un sel unique par utilisateur, jamais en clair)',
              "Numéro de téléphone du représentant légal de l'entreprise ou du fondateur inscrit",
              "Fonction au sein de l'entreprise",
            ]} />

            <SubTitle>2.2 Données relatives au dossier de candidature</SubTitle>
            <Def label="Finalité">réception, instruction, analyse et sélection des dossiers de candidature aux Trophées de l'innovation 2026.</Def>
            <Def label="Base légale">exécution d'un contrat ou de mesures précontractuelles et licéité du traitement (article 6.1.b RGPD).</Def>
            <P style={{ marginBottom: 4 }}>Données collectées :</P>
            <Bullets items={[
              "Données d'identification de l'entreprise : dénomination sociale, forme juridique, RCS/SIRET, adresse du siège social, date de création",
              'Données financières : chiffre d\'affaires, montant et historique des levées de fonds, principaux indicateurs financiers',
              'Business plan : présentation de la solution, analyse de marché, modèle économique, stratégie de développement',
              "CV et données des dirigeants : parcours professionnel, formations, expériences des fondateurs et membres de l'équipe dirigeante",
              'Données extra-financières : données ESG, impact environnemental, maturité technologique (TRL), indicateurs sociaux',
              'Tout document complémentaire transmis dans le cadre du dossier de candidature',
            ]} />
            <Callout small>
              Ces données présentent un caractère stratégique et confidentiel. Elles sont traitées avec le plus haut niveau
              de protection et ne sont accessibles qu'aux personnes strictement habilitées au sein d'Investance Partners,
              ainsi qu'aux membres du jury signataires d'un engagement de confidentialité.
            </Callout>

            <SubTitle>2.3 Données relatives aux communications</SubTitle>
            <Def label="Finalité">vous informer du suivi de votre candidature, des résultats de la sélection et des communications relatives aux Trophées.</Def>
            <Def label="Base légale">intérêt légitime d'Investance Partners (article 6.1.f RGPD) — vous avez candidaté et attendez légitimement d'être informé.</Def>
            <Def label="Données utilisées">nom, prénom, adresse e-mail professionnelle.</Def>

            <SubTitle>2.4 Données de connexion et de navigation</SubTitle>
            <Def label="Finalité">assurer le bon fonctionnement technique de la plateforme et garantir la sécurité des comptes.</Def>
            <Def label="Base légale">intérêt légitime d'Investance Partners (article 6.1.f RGPD).</Def>
            <P style={{ marginBottom: 4 }}>Données collectées :</P>
            <Bullets items={[
              'Adresse IP',
              'Date et heure de connexion',
              "Logs d'activité sur la plateforme",
              "Type de navigateur et système d'exploitation",
            ]} />
            <P>
              Par souci de transparence, nous vous informons que la plateforme <strong>n'utilise aucun cookie</strong>.
              L'authentification et certaines préférences d'affichage utilisent un mécanisme de stockage local du
              navigateur (<em>localStorage</em>) strictement fonctionnel, stockant : un jeton d'authentification
              valable 24 heures, un cache de votre profil utilisateur, votre préférence d'affichage (clair/sombre)
              et l'état de progression de votre candidature. Ces données restent sur votre appareil et ne sont
              pas transmises à des tiers.
            </P>
          </Section>

          {/* §3 */}
          <Section id={3} title="Destinataires des données">
            <P>Vos données personnelles sont accessibles uniquement aux personnes suivantes, dans la limite stricte de leurs besoins :</P>
            <Bullets items={[
              "L'équipe organisatrice des Trophées de l'innovation 2026 au sein d'Investance Partners, dans la limite de leurs habilitations respectives",
              "Les membres du jury des Trophées, uniquement pour les dossiers présélectionnés et après signature d'un engagement de confidentialité — ils accèdent aux dossiers de candidature mais non aux données de connexion",
              "La référente RGPD, Pauline Gargallo, dans le cadre de l'exercice de ses missions",
            ]} />
            <P>
              Vos données <strong>ne sont jamais vendues, louées ou cédées à des tiers</strong>. Les données des candidats
              présélectionnés (30 dossiers retenus) sont transmises à LBP AM, en sa qualité de destinataire, aux seules
              fins de sélection finale. Les données des candidats non présélectionnés ne lui sont pas communiquées.
            </P>
            <Callout small>
              Vos données ne font l'objet d'<strong>aucun transfert</strong> en dehors de l'Espace économique européen.
              La plateforme Paradoxe.AI est hébergée exclusivement sur des serveurs situés en France.
            </Callout>
          </Section>

          {/* §4 */}
          <Section id={4} title="Durées de conservation">
            <P>Vos données sont conservées pour les durées suivantes :</P>
            <div style={{ overflowX: 'auto', margin: '12px 0 16px' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Catégorie de données</th>
                    <th style={thStyle}>Durée de conservation</th>
                    <th style={thStyle}>Fondement</th>
                  </tr>
                </thead>
                <tbody>
                  {RETENTION_ROWS.map((row, i) => (
                    <tr key={i} style={{ background: i % 2 ? '#F7F9FC' : 'white' }}>
                      <td style={tdStyle}>{row[0]}</td>
                      <td style={tdStyle}>{row[1]}</td>
                      <td style={tdStyle}>{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <P>
              À l'expiration des délais de conservation, vos données font l'objet d'une suppression définitive ou d'une
              anonymisation irréversible.
            </P>
          </Section>

          {/* §5 */}
          <Section id={5} title="Sécurité des données">
            <P>
              Investance Partners met en œuvre des mesures techniques et organisationnelles appropriées pour assurer
              la sécurité, la confidentialité, l'intégrité et la disponibilité de vos données, notamment :
            </P>
            <Bullets items={[
              'Hébergement exclusif sur des serveurs situés en France (plateforme Paradoxe.AI — infrastructure souveraine)',
              "Absence de transfert de données hors de l'Espace économique européen",
              "Contrôle d'accès strict par authentification et gestion des habilitations",
              'Stockage des mots de passe sous forme hachée (algorithme bcrypt, sel unique par utilisateur, conforme aux recommandations OWASP et ANSSI) — jamais en clair',
              "Engagement de confidentialité signé par les membres du jury avant tout accès aux dossiers",
              'Accès aux données limité au strict besoin opérationnel',
              'Chiffrement des communications entre votre navigateur et la plateforme via le protocole HTTPS/TLS',
            ]} />
          </Section>

          {/* §6 */}
          <Section id={6} title="Vos droits">
            <P>Conformément au RGPD, vous disposez des droits suivants sur vos données personnelles :</P>
            <div style={{ overflowX: 'auto', margin: '12px 0 16px' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Droit</th>
                    <th style={thStyle}>Ce que vous pouvez faire</th>
                    <th style={thStyle}>Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {RIGHTS_ROWS.map((row, i) => {
                    const href = row[2].startsWith('www.cnil.fr') ? 'https://www.cnil.fr' : `mailto:${row[2]}`;
                    const target = href.startsWith('http') ? '_blank' : undefined;
                    return (
                      <tr key={i} style={{ background: i % 2 ? '#F7F9FC' : 'white' }}>
                        <td style={tdStyle}>{row[0]}</td>
                        <td style={tdStyle}>{row[1]}</td>
                        <td style={tdStyle}>
                          <a href={href} className="ml-link" {...(target ? { target, rel: 'noopener noreferrer' } : {})}>{row[2]}</a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <P>
              Pour exercer vos droits, adressez votre demande par e-mail à{' '}
              <a href="mailto:rgpd@investance-partners.com" className="ml-link">rgpd@investance-partners.com</a>,
              en précisant votre identité et la nature de votre demande. Nous nous engageons à vous répondre dans un
              délai d'un (1) mois à compter de la réception de votre demande.
            </P>
            <P>
              Si vous estimez que le traitement de vos données personnelles n'est pas conforme au RGPD, vous avez le
              droit d'introduire une réclamation auprès de la Commission Nationale de l'Informatique et des Libertés
              (CNIL) — 3 Place de Fontenoy, TSA 80715, 75334 Paris Cedex 07 —{' '}
              <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="ml-link">www.cnil.fr</a>.
            </P>
          </Section>

          {/* §7 */}
          <Section id={7} title="Cookies">
            <P>
              La plateforme Trophées de l'innovation 2026 <strong>ne dépose aucun cookie</strong> sur votre terminal,
              ni à des fins techniques, ni à des fins de mesure d'audience ou de profilage. Aucun bandeau de
              consentement n'est donc requis. Les fonctionnalités d'authentification et de mémorisation des
              préférences reposent exclusivement sur le <em>localStorage</em> du navigateur (voir §2.4). Ces
              informations sont strictement nécessaires au fonctionnement du service que vous demandez et bénéficient
              à ce titre de l'exemption de consentement prévue par la recommandation « cookies et autres traceurs »
              de la CNIL.
            </P>
            <SubTitle>Ressources tierces chargées par votre navigateur</SubTitle>
            <P style={{ marginBottom: 4 }}>
              Pour assurer le rendu graphique de la plateforme, votre navigateur établit des connexions vers les
              ressources externes suivantes, sans qu'aucun cookie ne soit déposé à cette occasion :
            </P>
            <Bullets items={[
              <>
                <strong>Google Fonts</strong> (<code>fonts.googleapis.com</code> et <code>fonts.gstatic.com</code>) pour
                le chargement des polices de caractères. Cette connexion implique la transmission de votre adresse IP
                à Google LLC, société établie aux États-Unis, sans dépôt de cookie. Google déclare ne pas utiliser
                cette donnée à des fins de traçage (politique Google Fonts depuis 2022).
              </>,
              <>
                <strong>OpenStreetMap</strong> (Fondation OpenStreetMap, Royaume-Uni / Union européenne) pour
                l'affichage des tuiles cartographiques utilisées dans l'espace jury. Cette connexion implique la
                transmission de votre adresse IP à la Fondation, sans dépôt de cookie ni dispositif de traçage.
              </>,
            ]} />
            <P>
              L'opportunité d'auto-héberger les polices Google Fonts sur l'infrastructure d'Investance Partners est en
              cours d'évaluation, afin de supprimer toute transmission de données vers un destinataire situé hors de
              l'Union européenne. Cette politique de confidentialité sera mise à jour en conséquence.
            </P>
          </Section>

          {/* §8 */}
          <Section id={8} title="Propriété des données de candidature">
            <P>
              Vous restez propriétaire et responsable de l'ensemble des informations, documents, solutions et
              innovations transmis dans votre dossier de candidature. La transmission de votre dossier via la
              plateforme ne confère à Investance Partners <strong>aucun droit de propriété intellectuelle</strong> sur
              les éléments transmis.
            </P>
            <P>
              Vos données de candidature sont utilisées exclusivement aux fins de la sélection des lauréats des
              Trophées de l'innovation 2026 et ne sont en aucun cas utilisées à des fins commerciales. En soumettant
              votre candidature, vous acceptez que les seules données relatives à votre dossier de candidature
              puissent être communiquées aux membres du jury, ainsi qu'à LBP AM pour les seuls dossiers
              présélectionnés (30 finalistes), aux fins de délibération et de sélection finale. Aucune autre
              communication à des tiers n'est effectuée sans votre accord préalable.
            </P>
          </Section>

          {/* §9 */}
          <Section id={9} title="Modifications de la politique de confidentialité">
            <P>
              La présente politique de confidentialité peut être modifiée à tout moment pour tenir compte d'évolutions
              réglementaires ou de nouvelles pratiques de traitement. En cas de modification substantielle, les
              utilisateurs disposant d'un compte seront informés par e-mail avec un préavis raisonnable.
            </P>
            <P>
              La date de dernière mise à jour figure en bas du présent document. Nous vous invitons à consulter
              régulièrement cette politique.
            </P>
          </Section>

          {/* Effective + last-update */}
          <div style={{
            marginTop: 28, padding: '20px 24px', borderRadius: 10,
            background: 'white', border: '1px solid #E3E7F2',
            color: '#555', fontSize: 13.5, lineHeight: 1.7, textAlign: 'center',
          }}>
            <div>Politique de confidentialité entrée en vigueur le : <strong style={{ color: NAVY }}>{EFFECTIVE_DATE}</strong></div>
            <div>Dernière mise à jour : <strong style={{ color: NAVY }}>{LAST_UPDATE}</strong></div>
            <div style={{ marginTop: 10, fontSize: 12, color: '#888' }}>
              Responsable : Investance Partners, 31 rue Saint-Augustin, 75002 Paris
            </div>
          </div>

          {/* CTA */}
          <div style={{ marginTop: 32, textAlign: 'center', display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/" style={{
              display: 'inline-block', padding: '10px 24px',
              border: `1px solid ${BLUE}`, color: BLUE,
              borderRadius: 999, textDecoration: 'none',
              fontFamily: 'Lato, sans-serif', fontWeight: 600, fontSize: 14,
            }}>← Retour à l'accueil</Link>
            <Link to="/mentions-legales" style={{
              display: 'inline-block', padding: '10px 24px',
              border: `1px solid ${NAVY}`, color: NAVY,
              borderRadius: 999, textDecoration: 'none',
              fontFamily: 'Lato, sans-serif', fontWeight: 600, fontSize: 14,
            }}>Mentions légales</Link>
            <Link to="/cgu" style={{
              display: 'inline-block', padding: '10px 24px',
              border: `1px solid ${NAVY}`, color: NAVY,
              borderRadius: 999, textDecoration: 'none',
              fontFamily: 'Lato, sans-serif', fontWeight: 600, fontSize: 14,
            }}>CGU</Link>
            <Link to="/reglement" style={{
              display: 'inline-block', padding: '10px 24px',
              border: `1px solid ${NAVY}`, color: NAVY,
              borderRadius: 999, textDecoration: 'none',
              fontFamily: 'Lato, sans-serif', fontWeight: 600, fontSize: 14,
            }}>Règlement des trophées de l'innovation 2026</Link>
          </div>
        </div>
      </section>

      {/* Inline styles for the ml-link class (shared with MentionsLegalesPage) */}
      <style>{`
        .ml-link {
          color: ${BLUE};
          text-decoration: none;
          border-bottom: 1px solid transparent;
          transition: color 0.15s, border-color 0.15s;
          word-break: break-word;
        }
        .ml-link:hover { color: ${CYAN}; border-bottom-color: ${CYAN}; }
        .ml-link:focus-visible {
          outline: 2px solid ${CYAN};
          outline-offset: 2px;
          border-radius: 2px;
        }
        .pc-section code {
          background: #F0F4FB;
          padding: 1px 6px;
          border-radius: 4px;
          font-size: 90%;
          color: ${NAVY};
        }
      `}</style>
    </div>
  );
}

// ===== Small typed building blocks =====

function Section({ id, title, children }) {
  return (
    <article className="pc-section" style={{
      background: 'white', borderRadius: 14,
      padding: '28px 32px', marginBottom: 18,
      boxShadow: '0 1px 3px rgba(27,30,56,0.06), 0 0 0 1px rgba(27,30,56,0.04)',
    }}>
      <h2 style={{
        fontFamily: 'Lato, sans-serif', fontSize: 20, fontWeight: 700,
        color: NAVY, marginTop: 0, marginBottom: 16,
        display: 'flex', alignItems: 'baseline', gap: 10,
      }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 28, height: 28, borderRadius: '50%',
          background: BLUE, color: 'white', fontSize: 13, fontWeight: 700,
          flexShrink: 0,
        }}>{id}</span>
        {title}
      </h2>
      {children}
    </article>
  );
}

function SubTitle({ children }) {
  return (
    <h3 style={{
      fontFamily: 'Lato, sans-serif', fontSize: 15.5, fontWeight: 700,
      color: NAVY, margin: '20px 0 10px',
    }}>{children}</h3>
  );
}

function P({ children, style }) {
  return (
    <p style={{ fontSize: 14.5, lineHeight: 1.7, color: '#333', margin: '0 0 12px', ...style }}>
      {children}
    </p>
  );
}

function Def({ label, children, href }) {
  return (
    <p style={{ fontSize: 14, lineHeight: 1.6, color: '#333', margin: '4px 0' }}>
      <strong style={{ color: NAVY }}>{label} :</strong>{' '}
      {href ? (
        <a href={href} className="ml-link"
           {...(href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
          {children}
        </a>
      ) : children}
    </p>
  );
}

function Bullets({ items }) {
  return (
    <ul style={{ margin: '6px 0 14px 0', paddingLeft: 22, lineHeight: 1.7, color: '#333', fontSize: 14.5 }}>
      {items.map((it, i) => <li key={i} style={{ marginBottom: 4 }}>{it}</li>)}
    </ul>
  );
}

function Callout({ children, small }) {
  return (
    <div style={{
      background: '#F0F4FB', borderLeft: `4px solid ${BLUE}`,
      padding: small ? '12px 16px' : '14px 18px',
      borderRadius: 8, margin: small ? '12px 0' : '0 0 18px',
      color: NAVY, fontSize: small ? 13.5 : 14, lineHeight: 1.6,
    }}>{children}</div>
  );
}

// ===== Shared styles =====
const navLinkStyle = {
  fontFamily: 'Lato, sans-serif', fontSize: 14, fontWeight: 600,
  padding: '9px 18px', borderRadius: 999, textDecoration: 'none',
  color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap',
};

const tableStyle = {
  width: '100%', borderCollapse: 'collapse', fontSize: 13.5,
  border: '1px solid #E3E7F2',
};
const thStyle = {
  background: NAVY, color: 'white', textAlign: 'left',
  padding: '10px 12px', fontFamily: 'Lato, sans-serif', fontWeight: 700,
  borderBottom: '1px solid rgba(255,255,255,0.15)',
};
const tdStyle = {
  padding: '10px 12px', color: '#333', verticalAlign: 'top',
  borderTop: '1px solid #E3E7F2',
};
